import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
// Side effect
import './shaders/bloom_downsample.fragment.js';
import './shaders/bloom_blur.fragment.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
const LUMINANCE_CUT_RANGE = 'luminanceRange';
const LUMINANCE_RANGE_EXPONENT = 'luminanceRangeExponent';
const BLUR_DIRECTION = 'direction';
const SOURCE_TEXTURE = 'sourceSampler';
const BLURRED_INPUT_TEXTURE = 'blurredInput';
const DOWNSAMPLE_UV_SCALE = 'uvScale';
const DOWNSAMPLE_DEPTH_TEXTURE = 'depthInput';
export class BloomEffect {
    constructor(scene, framebuffer, gpu_profiler) {
        this._downsampled_textures = [];
        this._blur_input_texture = null;
        this._blur_input_direction = { x: 0, y: 0 };
        this._downsample_uv_scale = 1.0;
        this._shader_source_texture = null;
        this._downsample_factor = 5;
        this.intensity = 1.0;
        this.min_luminance = 0.5;
        this.max_luminance = 500.0;
        this.luminance_exponent = 0.25;
        this._profiling = null;
        if (gpu_profiler !== null) {
            this._profiling = {
                profiler: gpu_profiler,
                section_total: gpu_profiler.create_section('Bloom total'),
                section_downsample: gpu_profiler.create_section('Bloom downsample'),
                section_blur: gpu_profiler.create_section('Bloom blur'),
                section_composite: gpu_profiler.create_section('Bloom composite')
            };
        }
        this._input_framebuffer = framebuffer;
        this._post_process_camera = new BABYLON_Camera('bloom_postprocess_camera', new BABYLON_Vector3(0, 0, 0), scene);
        const downsampled_texture_count = BloomEffect.BLOOM_LEVELS_COUNT;
        const blurred_texture_names = [];
        for (let i = 1; i <= downsampled_texture_count; i++) {
            const downsampled_width = Math.max(1, framebuffer.get_width() / Math.pow(this._downsample_factor, i));
            const downsampled_height = Math.max(1, framebuffer.get_height() / Math.pow(this._downsample_factor, i));
            this._downsampled_textures.push(new BABYLON_RenderTargetTexture('bloom_downsampled_' + i, { width: downsampled_width, height: downsampled_height }, scene, false, undefined, BABYLON_Engine.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV, false, BABYLON_Engine.TEXTURE_BILINEAR_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RGB, false, 1));
            blurred_texture_names.push(BLURRED_INPUT_TEXTURE + '[' + (i - 1) + ']');
        }
        this._blur_backbuffer = new BABYLON_RenderTargetTexture('bloom_blur_backbuffer', { width: framebuffer.get_width() / this._downsample_factor, height: framebuffer.get_height() / this._downsample_factor }, scene, false, undefined, BABYLON_Engine.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV, false, BABYLON_Engine.TEXTURE_BILINEAR_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RGB, false, 1);
        this._downsampled_textures.forEach((render_target) => {
            render_target.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
            render_target.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        });
        this._blur_backbuffer.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._blur_backbuffer.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._blur_effect = new BABYLON_PostProcess('bloom_blur', 'bloom_blur', [
            BLUR_DIRECTION
        ], [
            SOURCE_TEXTURE
        ], { width: 32, height: 32 }, // Dummy data
        this._post_process_camera, undefined, scene.getEngine(), true);
        this._blur_effect.onApply = (effect) => {
            effect.setFloat2(BLUR_DIRECTION, this._blur_input_direction.x, this._blur_input_direction.y);
            effect.setTexture(SOURCE_TEXTURE, this._blur_input_texture);
        };
        const downsample_on_apply = (effect) => {
            if (this._shader_source_texture == null) {
                console.error('Trying to render bloom blur without input texture.');
            }
            effect.setTexture(SOURCE_TEXTURE, this._shader_source_texture);
            effect.setFloat2(BLUR_DIRECTION, this._blur_input_direction.x, this._blur_input_direction.y);
            effect.setFloat(DOWNSAMPLE_UV_SCALE, this._downsample_uv_scale);
        };
        this._downsample_and_filter_effect = new BABYLON_PostProcess('bloom_downsample_and_filter', 'bloom_downsample', [
            LUMINANCE_CUT_RANGE,
            LUMINANCE_RANGE_EXPONENT,
            BLUR_DIRECTION,
            DOWNSAMPLE_UV_SCALE
        ], [
            SOURCE_TEXTURE, DOWNSAMPLE_DEPTH_TEXTURE
        ], { width: 32, height: 32 }, // Dummy data
        this._post_process_camera, undefined, scene.getEngine(), true, '#define DOWNSAMPLE_FACTOR ' + this._downsample_factor + '\n#define ENABLE_FILTERING');
        this._downsample_and_filter_effect.externalTextureSamplerBinding = true;
        this._downsample_and_filter_effect.onApply = (effect) => {
            downsample_on_apply(effect);
            effect.setFloat2(LUMINANCE_CUT_RANGE, this.min_luminance, this.max_luminance);
            effect.setFloat(LUMINANCE_RANGE_EXPONENT, this.luminance_exponent);
            effect.setTexture(DOWNSAMPLE_DEPTH_TEXTURE, this._input_framebuffer.get_depth_texture());
        };
        this._downsample_effect = new BABYLON_PostProcess('bloom_downsample', 'bloom_downsample', [
            BLUR_DIRECTION,
            DOWNSAMPLE_UV_SCALE
        ], [
            SOURCE_TEXTURE
        ], { width: 32, height: 32 }, // Dummy data
        this._post_process_camera, undefined, scene.getEngine(), true, '#define DOWNSAMPLE_FACTOR ' + this._downsample_factor);
        this._downsample_effect.externalTextureSamplerBinding = true;
        this._downsample_effect.onApply = downsample_on_apply;
    }
    get result_textures() {
        return this._downsampled_textures;
    }
    render(scene) {
        var _a, _b, _c, _d, _e, _f;
        if (this.intensity === 0.0) {
            // If our effect will have no effect on the image, skip rendering.
            return;
        }
        (_a = this._profiling) === null || _a === void 0 ? void 0 : _a.section_total.start();
        (_b = this._profiling) === null || _b === void 0 ? void 0 : _b.section_downsample.start();
        for (let i = 0; i < this._downsampled_textures.length; i++) {
            this._shader_source_texture = i === 0 ? this._input_framebuffer.get_texture() : this._downsampled_textures[i - 1];
            const vertical_effect = i === 0 ? this._downsample_and_filter_effect : this._downsample_effect;
            const horizontal_effect = this._downsample_effect;
            this._blur_input_direction.x = 0;
            this._blur_input_direction.y = 1;
            this._downsample_uv_scale = this._downsample_factor;
            this._blur_backbuffer._bindFrameBuffer();
            scene.postProcessManager.directRender([vertical_effect], null, false, undefined, undefined, true);
            this._shader_source_texture = this._blur_backbuffer;
            this._downsample_uv_scale = 1.0;
            this._blur_input_direction.x = 1;
            this._blur_input_direction.y = 0;
            scene.postProcessManager.directRender([horizontal_effect], this._downsampled_textures[i].renderTarget, true);
        }
        (_c = this._profiling) === null || _c === void 0 ? void 0 : _c.section_downsample.stop();
        (_d = this._profiling) === null || _d === void 0 ? void 0 : _d.section_blur.start();
        // The for loop here is in reverse to accomodate that we want to start with the smallest render target,
        // as we use the same full-size "backbuffer" for all passes. If we do the biggest one first, we will get bleeding in the smaller render targets.
        for (let i = this._downsampled_textures.length - 1; i >= 0; i--) {
            this._blur_effect.width = this._downsampled_textures[i].getSize().width;
            this._blur_effect.height = this._downsampled_textures[i].getSize().height;
            this._blur_input_texture = this._downsampled_textures[i];
            this._blur_input_direction.x = 0;
            this._blur_input_direction.y = 1;
            this._blur_backbuffer._bindFrameBuffer();
            scene.getEngine()._gl.viewport(0, 0, this._blur_effect.width, this._blur_effect.height);
            scene.postProcessManager.directRender([this._blur_effect], null, false, undefined, undefined, true);
            this._blur_input_texture = this._blur_backbuffer;
            this._blur_input_direction.x = 1;
            this._blur_input_direction.y = 0;
            scene.postProcessManager.directRender([this._blur_effect], this._downsampled_textures[i].renderTarget, true);
        }
        (_e = this._profiling) === null || _e === void 0 ? void 0 : _e.section_blur.stop();
        (_f = this._profiling) === null || _f === void 0 ? void 0 : _f.section_total.stop();
    }
    resize(width, height) {
        for (let i = 0; i < this._downsampled_textures.length; i++) {
            const downsampled_width = Math.max(1, width / Math.pow(this._downsample_factor, i + 1));
            const downsampled_height = Math.max(1, height / Math.pow(this._downsample_factor, i + 1));
            this._downsampled_textures[i].resize({ width: downsampled_width, height: downsampled_height });
        }
        this._blur_backbuffer.resize({ width: width / this._downsample_factor, height: height / this._downsample_factor });
    }
    dispose() {
        this._downsample_effect.dispose();
        this._blur_effect.dispose();
        this._blur_backbuffer.dispose();
        this._downsampled_textures.forEach((render_target) => {
            render_target.dispose();
        });
        this._downsampled_textures.length = 0;
        this._post_process_camera.dispose();
        this._blur_input_texture = null;
        this._shader_source_texture = null;
    }
}
BloomEffect.BLOOM_LEVELS_COUNT = 3;
//# sourceMappingURL=Bloom.js.map